import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ScrollIndicator } from './TerminalEffects';
import { trackButtonClick } from '../lib/analytics';

const phrases = [
  { prefix: 'Generate', suffix: 'More Leads' },
  { prefix: 'Generate', suffix: 'More Bookings' },
  { prefix: 'Generate', suffix: 'More Revenue' },
  { prefix: '', suffix: 'Build Your AI Receptionist' },
];

const codeLines = [
  'module hibotics::receptionist {',
  '    /// Your AI answers every call.',
  '    public fun answer_call(): bool {',
  '        let caller = get_incoming();',
  '        let intent = classify(caller);',
  '        schedule_appointment(intent);',
  '        return true;',
  '    }',
  '}',
  '',
  'module hibotics::booking {',
  '    /// 24/7 intelligent scheduling.',
  '    public fun auto_schedule(): bool {',
  '        let slot = find_available();',
  '        confirm_with_client(slot);',
  '        send_reminder(slot);',
  '        return true;',
  '    }',
  '}',
  '',
  'module hibotics::leads {',
  '    /// Qualify every lead instantly.',
  '    public fun qualify_lead(): bool {',
  '        let lead = capture_incoming();',
  '        let score = analyze(lead);',
  '        route_to_team(lead, score);',
  '        return true;',
  '    }',
  '}',
  '',
  'module hibotics::voice {',
  '    /// Natural conversations.',
  '    public fun engage_caller(): bool {',
  '        let ctx = load_context();',
  '        let reply = generate(ctx);',
  '        deliver_response(reply);',
  '        return true;',
  '    }',
  '}',
  '',
  'module hibotics::analytics {',
  '    /// Insights that drive growth.',
  '    public fun report(): bool {',
  '        let data = aggregate_calls();',
  '        let insights = extract(data);',
  '        push_to_dashboard(insights);',
  '        return true;',
  '    }',
  '}',
  '',
  'module hibotics::followup {',
  '    /// No lead left behind.',
  '    public fun send_followup(): bool {',
  '        let pending = get_pending();',
  '        let msg = craft_message(pending);',
  '        dispatch_sms(msg);',
  '        return true;',
  '    }',
  '}',
];

const TypewriterText = () => {
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [displayedPrefix, setDisplayedPrefix] = useState('');
  const [displayedSuffix, setDisplayedSuffix] = useState('');
  const [phase, setPhase] = useState('typePrefix'); // typePrefix | typeSuffix | pause | deleteSuffix | deletePrefix

  // Use refs to avoid excessive callback dependencies
  const stateRef = useRef({ phraseIndex: 0, displayedPrefix: '', displayedSuffix: '', phase: 'typePrefix' });
  stateRef.current = { phraseIndex, displayedPrefix, displayedSuffix, phase };

  const tick = useCallback(() => {
    const { phraseIndex: pi, displayedPrefix: dp, displayedSuffix: ds, phase: ph } = stateRef.current;
    const cur = phrases[pi];
    const nxt = phrases[(pi + 1) % phrases.length];
    const needsDel = cur.prefix !== nxt.prefix;

    if (ph === 'typePrefix') {
      if (dp.length < cur.prefix.length) {
        setDisplayedPrefix(cur.prefix.substring(0, dp.length + 1));
      } else {
        setPhase('typeSuffix');
      }
    } else if (ph === 'typeSuffix') {
      if (ds.length < cur.suffix.length) {
        setDisplayedSuffix(cur.suffix.substring(0, ds.length + 1));
      } else {
        setPhase('pause');
      }
    } else if (ph === 'pause') {
      return; // handled by the effect below
    } else if (ph === 'deleteSuffix') {
      if (ds.length > 0) {
        setDisplayedSuffix(cur.suffix.substring(0, ds.length - 1));
      } else if (needsDel) {
        setPhase('deletePrefix');
      } else {
        setPhraseIndex((prev) => (prev + 1) % phrases.length);
        setPhase('typeSuffix');
      }
    } else if (ph === 'deletePrefix') {
      if (dp.length > 0) {
        setDisplayedPrefix(cur.prefix.substring(0, dp.length - 1));
      } else {
        setPhraseIndex((prev) => (prev + 1) % phrases.length);
        setPhase('typePrefix');
      }
    }
  }, []); // stable — reads from ref

  // When phraseIndex changes and we're about to type a new prefix, check if prefix is same
  useEffect(() => {
    const cur = phrases[phraseIndex];
    if (phase === 'typeSuffix' && displayedPrefix === cur.prefix) {
      // prefix already matches, just type suffix
    } else if (phase === 'typePrefix' && cur.prefix === '' && displayedPrefix === '') {
      setPhase('typeSuffix');
    }
  }, [phraseIndex, phase, displayedPrefix]);

  useEffect(() => {
    const speed = (phase === 'deleteSuffix' || phase === 'deletePrefix') ? 40 : 80;
    if (phase === 'pause') {
      const timer = setTimeout(() => setPhase('deleteSuffix'), 2000);
      return () => clearTimeout(timer);
    }
    const timer = setTimeout(tick, speed);
    return () => clearTimeout(timer);
  }, [tick, phase, displayedPrefix, displayedSuffix, phraseIndex]);

  const showBreak = displayedPrefix.length > 0;

  return (
    <span className="text-[#2A2A2A]">
      {displayedPrefix}
      {showBreak && <br />}
      {displayedSuffix}
      <span className="inline-block w-[3px] h-[0.85em] bg-[#2A2A2A] ml-1 align-middle animate-blink" />
    </span>
  );
};

const ScrollingCode = ({ lines, direction = 'up', speed = 80 }) => {
  const doubled = [...lines, ...lines];
  return (
    <div className="overflow-hidden h-full select-none pointer-events-none">
      <div
        className={direction === 'up' ? 'animate-code-up' : 'animate-code-down'}
        style={{ animationDuration: `${speed}s` }}
      >
        {doubled.map((line, i) => (
          <div
            key={`${direction}-${i}`}
            className="text-[11px] leading-[1.7] whitespace-pre font-mono"
            style={{
              color: line.includes('module') ? 'rgba(0,255,209,0.25)' :
                     line.includes('///') ? 'rgba(196,149,106,0.2)' :
                     line.includes('public') ? 'rgba(125,255,212,0.2)' :
                     'rgba(255,255,255,0.08)',
            }}
          >
            {line || '\u00A0'}
          </div>
        ))}
      </div>
    </div>
  );
};

export const HeroCarousel = () => {
  const navigate = useNavigate();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <section className="relative min-h-screen overflow-hidden bg-[#D4F7DC]" data-testid="hero-section">
      {/* Split background - right dark half only on md+ */}
      <div className="absolute inset-0">
        <div className="hidden md:block absolute top-0 right-0 w-1/2 h-full bg-[#1a1a1a] overflow-hidden">
          {/* Flowing gradient blobs */}
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-[#00FFD1]/[0.06] blur-[100px] animate-blob-drift" />
          <div className="absolute top-1/2 left-1/4 w-[400px] h-[400px] rounded-full bg-[#C4956A]/[0.05] blur-[80px] animate-blob-drift-slow" />
          <div className="absolute -bottom-24 right-1/4 w-[350px] h-[350px] rounded-full bg-[#7DFFD4]/[0.04] blur-[90px] animate-blob-drift-rev" />
          {/* Scrolling code overlay */}
          <div className="absolute inset-0 flex gap-6 px-8 pt-20 opacity-80">
            <div className="flex-1">
              <ScrollingCode lines={codeLines.slice(0, 30)} direction="up" speed={70} />
            </div>
            <div className="flex-1 hidden xl:block">
              <ScrollingCode lines={codeLines.slice(15)} direction="down" speed={85} />
            </div>
          </div>
          {/* Edge gradient for blending */}
          <div className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-[#1a1a1a] to-transparent z-10" />
        </div>
      </div>

      {/* Top fade */}
      <div className="absolute top-0 left-0 right-0 h-24 z-10 pointer-events-none" style={{ background: 'linear-gradient(to bottom, rgba(212,247,220,0.6) 0%, transparent 100%)' }} />
      <div className="hidden md:block absolute bottom-0 left-0 right-0 h-32 z-10 pointer-events-none" style={{ background: 'linear-gradient(to top, rgba(26,26,26,0.5) 0%, transparent 50%, transparent 100%)' }} />

      {/* Content */}
      <div className={`relative z-20 min-h-screen flex items-center transition-opacity duration-1000 ${mounted ? 'opacity-100' : 'opacity-0'}`}>
        <div className="w-full max-w-[1200px] mx-auto px-6 lg:px-8">
          <div className="max-w-full md:max-w-[480px] lg:max-w-[46%]">
            {/* Cycling headline */}
            <h1
              className="text-4xl sm:text-5xl md:text-6xl lg:text-[5rem] font-bold leading-[0.95] tracking-[-0.03em] mb-8"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              <TypewriterText />
            </h1>

            {/* Subtitle */}
            <p
              className="text-base sm:text-lg text-[#2A2A2A]/50 max-w-[480px] mb-10 leading-relaxed opacity-0"
              style={{
                animation: 'fadeSlideUp 0.8s ease-out 0.5s forwards',
                fontFamily: "'Manrope', sans-serif",
              }}
            >
              Your always-on AI voice agent that answers calls, books appointments, and qualifies leads — so you can focus on what matters.
            </p>

            {/* CTA */}
            <div className="opacity-0" style={{ animation: 'fadeSlideUp 0.8s ease-out 0.8s forwards' }}>
              <button
                onClick={() => { trackButtonClick('design_hibot_cta', 'hero'); navigate('/onboarding'); }}
                data-testid="hero-cta-button"
                className="bg-[#2A2A2A] text-white px-8 py-3.5 rounded-full text-xs font-bold uppercase tracking-[0.14em] hover:bg-black hover:scale-105 hover:shadow-[0_0_30px_rgba(0,255,209,0.2)] transition-all duration-300 flex items-center gap-2.5 group"
                style={{ fontFamily: "'Orbitron', sans-serif" }}
              >
                Design your Hi-Bot
                <ArrowRight className="group-hover:translate-x-1 transition-transform" size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <ScrollIndicator />

      {/* Keyframes */}
      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
        .animate-blink {
          animation: blink 0.8s step-end infinite;
        }
        @keyframes code-up {
          0% { transform: translateY(0); }
          100% { transform: translateY(-50%); }
        }
        @keyframes code-down {
          0% { transform: translateY(-50%); }
          100% { transform: translateY(0); }
        }
        .animate-code-up {
          animation: code-up linear infinite;
        }
        .animate-code-down {
          animation: code-down linear infinite;
        }
        @keyframes blob-drift {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -20px) scale(1.05); }
          66% { transform: translate(-20px, 30px) scale(0.95); }
        }
        @keyframes blob-drift-slow {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(-40px, 20px) scale(1.08); }
        }
        @keyframes blob-drift-rev {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(30px, -30px) scale(1.06); }
        }
        .animate-blob-drift { animation: blob-drift 12s ease-in-out infinite; }
        .animate-blob-drift-slow { animation: blob-drift-slow 16s ease-in-out infinite; }
        .animate-blob-drift-rev { animation: blob-drift-rev 14s ease-in-out infinite; }
      `}</style>
    </section>
  );
};
